import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import type { GamePassUserProgressResponse } from "../../types/gamePass";
import { fetchUserProgress } from "./useGetUserProgress";
import { retryOnTransientErrors } from "./queryUtils";

type UseGetUserProgressBySeasonIdsResult = {
  seasonProgressById: Readonly<Record<string, GamePassUserProgressResponse>>;
  isLoading: boolean;
  isFetching: boolean;
};

export const useGetUserProgressBySeasonIds = (
  userId?: string,
  seasonIds: ReadonlyArray<string> = [],
): UseGetUserProgressBySeasonIdsResult => {
  const uniqueSeasonIds = useMemo(
    () => Array.from(new Set(seasonIds.filter(Boolean))),
    [seasonIds],
  );

  const results = useQueries({
    queries: uniqueSeasonIds.map((seasonId) => ({
      queryKey: [
        "game-pass",
        "users",
        "progress",
        userId ?? "",
        "season",
        seasonId,
      ],
      queryFn: () => fetchUserProgress(userId!, seasonId),
      enabled: Boolean(userId && seasonId),
      staleTime: 1000 * 30,
      refetchOnWindowFocus: false,
      retry: retryOnTransientErrors,
    })),
  }) as ReadonlyArray<UseQueryResult<GamePassUserProgressResponse, Error>>;

  const seasonProgressById = useMemo(() => {
    const progressById: Record<string, GamePassUserProgressResponse> = {};

    uniqueSeasonIds.forEach((seasonId, index) => {
      const result = results[index];
      if (result?.isSuccess && result.data) {
        progressById[seasonId] = result.data;
      }
    });

    return progressById;
  }, [results, uniqueSeasonIds]);

  return {
    seasonProgressById,
    isLoading: results.some((result) => result.isLoading),
    isFetching: results.some((result) => result.isFetching),
  };
};
